import { useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  BasicWidgetForm,
  TableWidgetForm,
  ObjectWidgetForm,
  ChartWidgetForm,
  ColorCardWidgetForm,
  TraceWidgetForm,
} from "./components";
import { useWidgetSubmit } from "./useWidgetSubmit";
import { useGetLayoutDefQuery } from "store/api";

const WIDGET_LABELS: Record<string, string> = {
  BasicWidgetInput: "BasicWidgetInput",
  TableWidgetInput: "TableWidgetInput",
  ObjectWidgetInput: "ObjectWidgetInput",
  ChartWidgetInput: "ChartWidgetInput",
  ColorCardInput: "ColorCardInput",
  TraceWidgetInput: "TraceWidgetInput",
};

const WidgetInputPage: React.FC = () => {
  const { widgetType = "", layoutId } = useParams<{ widgetType: string; layoutId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleSubmit } = useWidgetSubmit(layoutId);
  const editWidgetId = searchParams.get("editWidgetId") ?? "";

  const { data: layoutDef } = useGetLayoutDefQuery(layoutId ?? "", {
    skip: !layoutId || !editWidgetId,
    refetchOnMountOrArgChange: true,
  });

  const decodedType = useMemo(() => {
    try {
      return decodeURIComponent(widgetType);
    } catch {
      return widgetType;
    }
  }, [widgetType]);

  const initialWidgetData = useMemo(() => {
    if (!editWidgetId) return null;
    const layoutItems = (layoutDef as { layout?: Array<{ i?: string; widget?: Record<string, any> }> } | null)?.layout;
    if (!Array.isArray(layoutItems)) return null;
    const found = layoutItems.find((item) => String(item?.i ?? "") === String(editWidgetId));
    return found?.widget ?? null;
  }, [editWidgetId, layoutDef]);

  const title = `${editWidgetId ? "Edit" : "Add"} ${WIDGET_LABELS[decodedType] ?? decodedType}`;

  const renderWidgetForm = () => {
    const backNavigation = () => navigate(-1);
    const onSubmit = async (data: Record<string, any>) => {
      try {
        await handleSubmit(data, editWidgetId || undefined);
        navigate(-1); // Navigate back on success
      } catch (error) {
        console.error("Widget submission failed:", error);
      }
    };

    if (decodedType === "BasicWidgetInput") return <BasicWidgetForm onBack={backNavigation} onSubmit={onSubmit} initialData={initialWidgetData} />;
    if (decodedType === "TableWidgetInput") return <TableWidgetForm onBack={backNavigation} onSubmit={onSubmit} initialData={initialWidgetData} />;
    if (decodedType === "ObjectWidgetInput") return <ObjectWidgetForm onBack={backNavigation} onSubmit={onSubmit} initialData={initialWidgetData} />;
    if (decodedType === "ChartWidgetInput") return <ChartWidgetForm onBack={backNavigation} onSubmit={onSubmit} initialData={initialWidgetData} />;
    if (decodedType === "ColorCardInput") return <ColorCardWidgetForm onBack={backNavigation} onSubmit={onSubmit} initialData={initialWidgetData} />;
    if (decodedType === "TraceWidgetInput") return <TraceWidgetForm onBack={backNavigation} onSubmit={onSubmit} initialData={initialWidgetData} />;

    return <text data-color="error-color">Unknown widget type: {decodedType}</text>;
  };

  return (
    <group data-height="auto" data-min-height="fit">
      <group data-direction="column" data-gap="10" data-space="10">
        <group data-border="" data-contain="" data-direction="column" data-wrap="no" data-height="autofit">
          <group data-space="10" data-direction="column" data-gap="15">
            <text data-text-size="x-large" data-weight="600">{title || "Widget"}</text>

            {renderWidgetForm()}
          </group>
        </group>
      </group>
    </group>
  );
};

export default WidgetInputPage;
